import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { updateConsent } from '../utils/consent';

const COOKIE_CONSENT_KEY = 'cookie_consent';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(parsed);
        // Re-apply the saved choice to Google Consent Mode on every load
        // (index.html defaults everything to "denied").
        updateConsent(parsed);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setPreferences(consent);
    setShowBanner(false);
    setShowPreferences(false);

    // Tell Google Consent Mode about the user's choice so Analytics is
    // actually enabled/disabled accordingly.
    updateConsent(consent);
  };

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true });
  };

  const acceptEssential = () => {
    saveConsent({ essential: true, analytics: false });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-bg-secondary border-t border-border-primary shadow-lg">
      {!showPreferences ? (
        // Main banner
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {t('cookies.bannerTitle')}
              </h3>
              <p className="text-sm text-text-secondary">
                {t('cookies.bannerMessage')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 text-sm font-medium text-text-secondary border border-border-primary rounded-lg hover:bg-bg-tertiary transition-colors"
              >
                {t('cookies.manageCookies')}
              </button>
              <button
                onClick={acceptEssential}
                className="px-4 py-2 text-sm font-medium text-text-primary border border-border-primary rounded-lg hover:bg-bg-tertiary transition-colors"
              >
                {t('cookies.acceptEssential')}
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-colors"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Preferences panel
        <div className="max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            {t('cookies.preferencesTitle')}
          </h3>

          <div className="space-y-4 mb-6">
            {/* Essential cookies - always on */}
            <div className="flex items-start justify-between p-4 bg-bg-tertiary rounded-lg">
              <div className="flex-1 mr-4">
                <h4 className="font-medium text-text-primary">
                  {t('cookies.essential')}
                </h4>
                <p className="text-sm text-text-secondary mt-1">
                  {t('cookies.essentialDescription')}
                </p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-5 w-5 rounded border-border-primary bg-bg-tertiary text-accent-primary cursor-not-allowed opacity-50"
                />
              </div>
            </div>

            {/* Analytics cookies */}
            <div className="flex items-start justify-between p-4 bg-bg-tertiary rounded-lg">
              <div className="flex-1 mr-4">
                <h4 className="font-medium text-text-primary">
                  {t('cookies.analytics')}
                </h4>
                <p className="text-sm text-text-secondary mt-1">
                  {t('cookies.analyticsDescription')}
                </p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="h-5 w-5 rounded border-border-primary bg-bg-tertiary text-accent-primary cursor-pointer"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <button
              onClick={() => setShowPreferences(false)}
              className="px-4 py-2 text-sm font-medium text-text-secondary border border-border-primary rounded-lg hover:bg-bg-tertiary transition-colors"
            >
              {t('common.back')}
            </button>
            <button
              onClick={savePreferences}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-colors"
            >
              {t('cookies.savePreferences')}
            </button>
          </div>

          <p className="text-xs text-text-muted mt-4 text-center">
            {t('cookies.learnMore')
              .split('<privacyLink>')
              .map((part, index) => {
                if (index === 0) return part;
                const [linkText, ...rest] = part.split('</privacyLink>');
                return (
                  <React.Fragment key={index}>
                    <Link
                      to="/privacy-policy"
                      className="text-accent-primary hover:text-accent-secondary underline"
                    >
                      {linkText}
                    </Link>
                    {rest.join('</privacyLink>')}
                  </React.Fragment>
                );
              })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CookieBanner;
